import React, { useMemo, useState } from "react";
import { Box } from "@mui/material";
import {
  SelectInputComponent,
  TextInputComponent,
} from "../../../../../../common/components";
import { Form, Formik } from "formik";
import CountryCodeWithPhoneNumber from "../../../../../../common/components/phone-number-input-component/country-code-phone-numner-input";
import "./edit-basic-details.scss";
import SearchableAutoSelect from "../../../../../../common/components/auto-select-search-component/search-select-component";
import { getTimezones } from "../../../../../../constants/timezones";
import {
  updateTenetDetails,
  updateTenetUsers,
  validatePartnerCompany,
} from "../../../../../../api/tenets";
import { EditBasicDetailsProps } from "../../../tenant-details-types";
import * as Yup from "yup";
import { useSetLoading } from "../../../../../../common/hooks/appearance";
import { debounce } from "lodash";
import { websiteUrlRegex } from "../../../../../../constants/regexStrings";

const PRODUCT_OPTIONS = [
  { label: "At-Need", value: "At-Need" },
  { label: "Preneed", value: "Preneed" },
];

const EditBasicDetails: React.FC<EditBasicDetailsProps> = ({
  handleCancel,
  tenantData,
  handleError,
  setTenantData,
  handleAPIsuccess,
}) => {
  const timezones = getTimezones();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isExistCompanyName, setIsExistCompanyName] = useState<boolean>(false);

  const superAdmin = tenantData?.users?.find(
    (user: any) => user?.is_primary_user
  );

  const validateCompanyName = useMemo(
    () =>
      debounce(async (companyName: string) => {
        try {
          setIsLoading(true);
          const resp: any = await validatePartnerCompany({
            companyName: companyName.trim(),
          });

          setIsExistCompanyName(!!resp?.data?.data?.exists);
        } catch (e: any) {
          console.error("Validation error:", e);
        } finally {
          setIsLoading(false);
        }
      }, 500),
    [setIsLoading, setIsExistCompanyName]
  );

  const initialValues = {
    firstName: superAdmin?.first_name || "-",
    lastName: superAdmin?.last_name || "-",
    companyName: tenantData?.basic_details?.company_name || "",
    companyWebsite: tenantData?.basic_details?.company_website || "",
    companyEmail: tenantData?.basic_details?.email || "",
    companyPhoneNumber: tenantData?.basic_details?.phone_number || "",
    email: superAdmin?.email || "",
    phoneNumber: superAdmin?.phone_number || "",
    timezone: tenantData?.basic_details?.timezone || null,
    employeeCount: tenantData?.basic_details?.employee_count || "",
    products: tenantData?.basic_details?.products || [],
  };

  const editBasicDetailsValidationSchema = Yup.object().shape({
    firstName: Yup.string().trim().required("First name is required"),

    lastName: Yup.string().trim().required("Last name is required"),

    email: Yup.string()
      .email("Invalid email format")
      .required("Email is required"),

    phoneNumber: Yup.string()
      .matches(
        /^\+\d{1,4}[\s]?\d{6,14}$/,
        "Enter valid phone number with country code"
      )
      .required("Phone number is required"),
    companyPhoneNumber: Yup.string()
      .matches(
        /^\+\d{1,4}[\s]?\d{6,14}$/,
        "Enter valid phone number with country code"
      )
      .required("Phone number is required"),
    companyName: Yup.string().trim().required("Company name is required"),

    companyWebsite: Yup.string()
      .matches(websiteUrlRegex, "Enter a valid website URL")
      .nullable()
      .notRequired(),

    timezone: Yup.mixed().nullable().required("Timezone is required"),
    companyEmail: Yup.string()
      .email("Invalid email format")
      .required("Email is required"),
    employeeCount: Yup.number()
      .min(1, "Employee count must be greater than 0")
      .nullable(),
  });

  const handleSubmit = async (values: any) => {
    setIsLoading(true);
    const companyPayload = {
      companyName: values.companyName,
      companyWebsite: values.companyWebsite,
      timezone: values.timezone || null,
      employeeCount: Number(values.employeeCount),
      email: values.companyEmail,
      phoneNumber: values.companyPhoneNumber,
      products: values.products,
    };

    const primaryUser = tenantData?.users?.find(
      (user: any) => user?.is_primary_user
    );

    if (!primaryUser) {
      console.error("Primary user not found");
      handleError("Primary user not found.");
      return;
    }

    const userPayload = {
      users: [
        {
          id: primaryUser.id,
          isUpdate: true,
          isPrimaryUser: true,
          roleId: primaryUser?.role?.id,
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.email,
          phoneNumber: values.phoneNumber,
        },
      ],
    };

    try {
      const [detailsResponse, userResponse] = await Promise.all([
        updateTenetDetails(tenantData?.id, "basicDetails", companyPayload),
        updateTenetUsers(tenantData?.id, userPayload),
      ]);

      const success =
        detailsResponse?.data?.success && userResponse?.data?.success;

      if (success) {
        const updatedDetails = {
          ...tenantData,
          basic_details: {
            ...tenantData.basic_details,
            company_name: values.companyName,
            company_website: values.companyWebsite,
            email: values.companyEmail,
            phone_number: values.companyPhoneNumber,
            timezone: values.timezone,
            employee_count: Number(values.employeeCount),
            products: values.products,
          },
          users: tenantData.users.map((user: any) =>
            user.is_primary_user
              ? {
                  ...user,
                  first_name: values.firstName,
                  last_name: values.lastName,
                  phone_number: values.phoneNumber,
                  email: values.email,
                }
              : user
          ),
        };

        setTenantData(updatedDetails);
        setIsLoading(false);
        setTimeout(() => {
          if (handleAPIsuccess) {
            handleAPIsuccess();
          }
          handleCancel();
        }, 100);
      } else {
        throw new Error("Update failed");
      }
    } catch (error) {
      setIsLoading(false);
      console.error("Error updating tenant details", error);
      handleError(error);
    }
  };

  const handleCompanyNameChange = (value: any, setFieldValue: any) => {
    setFieldValue("companyName", value);
    if (value && value.trim().length > 0) {
      validateCompanyName(value);
    } else {
      setIsExistCompanyName(false);
    }
  };

  const showLoading = useMemo(() => isLoading, [isLoading]);
  useSetLoading(showLoading);
  return (
    <>
      <Formik
        initialValues={initialValues}
        validationSchema={editBasicDetailsValidationSchema}
        validateOnBlur
        onSubmit={(values: any) => {
          if (!isExistCompanyName) handleSubmit(values);
        }}
      >
        {({
          values,
          errors,
          handleChange,
          validateForm,
          setTouched,
          setFieldValue,
          setFieldTouched,
          touched,
        }) => (
          <Form>
            <Box
              className="tenant-basic-details-content tenant-edit-basic-details-wrapper"
              gap={3}
            >
              <Box className="tenant-basic-details-content-header">
                <ul className="equal-width-list">
                  <li className="list-item">
                    <span className="item-label">First Name</span>
                    <span className="item-value">
                      <TextInputComponent
                        type="text"
                        value={values.firstName || ""}
                        placeholder="Enter first name"
                        onChange={handleChange}
                        fieldName="firstName"
                        maxlength="50"
                        disabled
                      />
                    </span>
                  </li>
                  <li className="list-item">
                    <span className="item-label">Last Name</span>
                    <span className="item-value">
                      <TextInputComponent
                        type="text"
                        value={values.lastName || ""}
                        placeholder="Enter last name"
                        onChange={handleChange}
                        fieldName="lastName"
                        maxlength="50"
                        disabled
                      />
                    </span>
                  </li>
                  <li className="list-item">
                    <span className="item-label">Email Address</span>
                    <span className="item-value">
                      <TextInputComponent
                        type="text"
                        value={values.email || ""}
                        placeholder="Enter email"
                        onChange={handleChange}
                        fieldName="email"
                        maxlength="150"
                      />
                    </span>
                  </li>
                  <li className="list-item">
                    <span className="item-label">Phone</span>
                    <span className="item-value">
                      <CountryCodeWithPhoneNumber
                        fieldName="phoneNumber"
                        placeholder="Enter phone"
                        onChange={(value: string, country: any) => {
                          const countryCode = country?.dialCode;
                          const numberOnly = value.slice(countryCode.length + 1);
                          const formatted = `+${countryCode} ${numberOnly}`;

                          setFieldValue("phoneNumber", formatted);
                        }}
                        onBlur={() => {
                          setFieldTouched("phoneNumber", true);
                        }}
                        value={values?.phoneNumber || ""}
                        defaultCountry="us"
                      />
                      {errors?.phoneNumber &&
                        touched?.phoneNumber &&
                        typeof errors.phoneNumber === "string" && (
                          <div className="error-message">
                            {errors?.phoneNumber || ""}
                          </div>
                        )}
                    </span>
                  </li>
                  <li className="list-item">
                    <span className="item-label">Products</span>
                    <span className="item-value">
                      <SelectInputComponent
                        fieldName="products"
                        options={PRODUCT_OPTIONS}
                        value={values.products || []}
                        onChange={(e: any) => setFieldValue("products", e)}
                        placeholder="Select product"
                        multiple={true}
                        tabs={true}
                      />
                    </span>
                  </li>
                </ul>
              </Box>
              <Box className="tenant-basic-details-content-header">
                <ul className="equal-width-list">
                  <li className="list-item">
                    <span className="item-label">Company Name</span>
                    <span className="item-value">
                      <TextInputComponent
                        type="text"
                        value={values.companyName || ""}
                        placeholder="Enter company name"
                        onChange={(e: any) => {
                          handleCompanyNameChange(
                            e.target.value,
                            setFieldValue
                          );
                        }}
                        optional
                        fieldName="companyName"
                        allowSpecialCharacters
                        maxlength="150"
                      />
                      {isExistCompanyName && (
                        <div className="error-message">
                          Company name already exists
                        </div>
                      )}
                    </span>
                  </li>
                  <li className="list-item">
                    <span className="item-label">Company Website</span>
                    <span className="item-value">
                      <TextInputComponent
                        type="text"
                        value={values.companyWebsite || ""}
                        placeholder="Enter company website"
                        onChange={handleChange}
                        fieldName="companyWebsite"
                        maxlength="150"
                      />
                    </span>
                  </li>
                  <li className="list-item">
                    <span className="item-label">Time Zone</span>
                    <span className="item-value time-zone-select">
                      <SearchableAutoSelect
                        fieldName="timezone"
                        optional
                        options={timezones}
                        isShowError={!values?.timezone}
                        placeholder="Select time zone"
                        value={
                          timezones.find(
                            (opt) => opt.value === values?.timezone
                          ) || null
                        }
                        onChange={(e: any) => {
                          setFieldValue("timezone", e);
                        }}
                        handleBlur={() => {
                          setFieldTouched("timezone", true);
                        }}
                      />
                    </span>
                  </li>
                  <li className="list-item">
                    <span className="item-label">Employee Count</span>
                    <span className="item-value">
                      <TextInputComponent
                        type="text"
                        value={values.employeeCount || ""}
                        placeholder="Enter employee count"
                        onChange={handleChange}
                        fieldName="employeeCount"
                        maxlength="6"
                        isNumber
                      />
                    </span>
                  </li>
                  <li className="list-item">
                    <span className="item-label">Company Email</span>
                    <span className="item-value">
                      <TextInputComponent
                        type="text"
                        value={values.companyEmail || ""}
                        placeholder="Enter email"
                        onChange={handleChange}
                        fieldName="companyEmail"
                        maxlength="150"
                      />
                    </span>
                  </li>
                  <li className="list-item">
                    <span className="item-label">Company Phone</span>
                    <span className="item-value">
                      <CountryCodeWithPhoneNumber
                        fieldName="companyPhoneNumber"
                        placeholder="Enter phone"
                        onChange={(value: string, country: any) => {
                          const countryCode = country?.dialCode;
                          const numberOnly = value.slice(countryCode.length + 1);
                          const formatted = `+${countryCode} ${numberOnly}`;

                          setFieldValue("companyPhoneNumber", formatted);
                        }}
                        onBlur={() => {
                          setFieldTouched("companyPhoneNumber", true);
                        }}
                        value={values?.companyPhoneNumber || ""}
                        defaultCountry="us"
                      />
                      {errors?.companyPhoneNumber &&
                        touched?.companyPhoneNumber &&
                        typeof errors.companyPhoneNumber === "string" && (
                          <div className="error-message">
                            {errors.companyPhoneNumber}
                          </div>
                        )}
                    </span>
                  </li>
                </ul>
              </Box>
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                paddingBottom: "20px",
                paddingRight: "10px",
              }}
            >
              <button
                type="button"
                className="cancel-button"
                onClick={handleCancel}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="cancel-button next-button-wrapper"
              >
                Save
              </button>
            </Box>
          </Form>
        )}
      </Formik>
    </>
  );
};

export default EditBasicDetails;
